import React, { useEffect, useRef } from 'react';

const GOOGLE_MAP_KEY = process.env.REACT_APP_GOOGLE_MAP_KEY;

const TrackingMap = ({ historyData, onPointClick }) => {
  const mapRef = useRef(null);

  useEffect(() => {
    if (!historyData || historyData.length === 0 || !mapRef.current) return;

    const hasGoogleMaps = Boolean(GOOGLE_MAP_KEY);
    const coordinates = historyData.map(point => ({
      lat: parseFloat(point.latitude),
      lng: parseFloat(point.longitude),
      timestamp: point.timestamp,
      address: point.address,
      speed: point.speed || 0
    }));

    let mapHtml = '';

    if (hasGoogleMaps) {
      mapHtml = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8" />
          <style>
            html, body { margin: 0; height: 100%; }
            #map { width: 100%; height: 100vh; }
            .gm-style-iw-chr, .gm-ui-hover-effect {
              display: none !important;
            }
            .gm-style-iw {
              padding: 0 !important;
            }
          </style>
        </head>
        <body>
          <div id="map"></div>
          <script src="https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAP_KEY}&libraries=geometry"></script>
          <script>
            const historyData = ${JSON.stringify(coordinates)};
            const waypointCoords = ${JSON.stringify(coordinates.slice(1, -1).slice(0, 23))};
            const map = new google.maps.Map(document.getElementById('map'), {
              mapTypeId: 'roadmap'
            });
            const bounds = new google.maps.LatLngBounds();
            let activeInfoWindow = null;
            const path = historyData.map(point => {
              const latLng = new google.maps.LatLng(point.lat, point.lng);
              bounds.extend(latLng);
              return latLng;
            });
            if (!bounds.isEmpty()) {
              map.fitBounds(bounds);
            } else {
              map.setCenter(path[0]);
              map.setZoom(15);
            }
            const lineSymbol = {
              path: 'M 0 -6 L 12 0 L 0 6 z',
              scale: 1.1,
              strokeColor: '#0f172a',
              strokeOpacity: 0.95,
              fillColor: '#0f172a',
              fillOpacity: 0.95
            };

            const toRad = (deg) => (deg * Math.PI) / 180;
            const computeBearing = (lat1, lng1, lat2, lng2) => {
              const φ1 = toRad(lat1);
              const φ2 = toRad(lat2);
              const Δλ = toRad(lng2 - lng1);
              const y = Math.sin(Δλ) * Math.cos(φ2);
              const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
              return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
            };

            const addSegmentArrows = () => {
              if (historyData.length < 2) return;
              for (let i = 0; i < historyData.length - 1; i++) {
                const start = historyData[i];
                const end = historyData[i + 1];
                if (!start || !end) continue;
                const midLat = (start.lat + end.lat) / 2;
                const midLng = (start.lng + end.lng) / 2;
                const heading = computeBearing(start.lat, start.lng, end.lat, end.lng);
                new google.maps.Marker({
                  position: { lat: midLat, lng: midLng },
                  map,
                  icon: {
                    path: 'M 0 -6 L 12 0 L 0 6 z',
                    fillColor: '#0f172a',
                    fillOpacity: 0.95,
                    strokeColor: '#ffffff',
                    strokeWeight: 1,
                    scale: 1.1,
                    rotation: heading,
                    anchor: new google.maps.Point(2, 0)
                  },
                  clickable: false,
                  zIndex: 3
                });
              }
            };

            const drawPolyline = () => {
              const polyline = new google.maps.Polyline({
                path,
                geodesic: true,
                strokeColor: '#0f172a',
                strokeOpacity: 0.95,
                strokeWeight: 3,
                icons: [{
                  icon: lineSymbol,
                  offset: '50%'
                }]
              });
              polyline.setMap(map);
            };

            const renderDirections = () => {
              if (historyData.length < 2) {
                drawPolyline();
                return;
              }

              const directionsService = new google.maps.DirectionsService();
              const directionsRenderer = new google.maps.DirectionsRenderer({
                map,
                suppressMarkers: true,
                polylineOptions: {
                  strokeColor: '#0f172a',
                  strokeOpacity: 0.95,
                  strokeWeight: 3,
                  icons: [{
                    icon: lineSymbol,
                    offset: '50%'
                  }]
                }
              });

              const waypoints = waypointCoords.map(pt => ({
                location: new google.maps.LatLng(pt.lat, pt.lng),
                stopover: false
              }));

              directionsService.route(
                {
                  origin: { lat: historyData[0].lat, lng: historyData[0].lng },
                  destination: { lat: historyData[historyData.length - 1].lat, lng: historyData[historyData.length - 1].lng },
                  waypoints,
                  travelMode: google.maps.TravelMode.DRIVING,
                  optimizeWaypoints: false
                },
                (response, status) => {
                  if (status === 'OK' && response) {
                    directionsRenderer.setDirections(response);
                  } else {
                    drawPolyline();
                  }
                }
              );
            };

            renderDirections();
            addSegmentArrows();

            historyData.forEach((point, index) => {
              const isStart = index === 0;
              const isEnd = index === historyData.length - 1;
              const color = isStart ? '#22c55e' : isEnd ? '#ef4444' : '#215F9A';
              const closeBtnId = 'gm-close-' + index + '-' + Math.random().toString(36).slice(2, 7);
              const formattedTime = new Date(point.timestamp).toLocaleTimeString();
              const rawSpeed = point.speed ?? 0;
              const numericSpeed = typeof rawSpeed === 'number' ? rawSpeed : Number(rawSpeed || 0);
              const formattedSpeed = (Number.isFinite(numericSpeed) ? numericSpeed : 0).toFixed(2);
              const marker = new google.maps.Marker({
                position: { lat: point.lat, lng: point.lng },
                map,
                label: isStart ? 'S' : isEnd ? 'E' : String(index + 1),
                icon: {
                  path: google.maps.SymbolPath.CIRCLE,
                  scale: 7,
                  fillColor: color,
                  fillOpacity: 1,
                  strokeColor: '#ffffff',
                  strokeWeight: 2
                }
              });
              const infoWindow = new google.maps.InfoWindow({
                content:
                  '<div style="font-family: &#39;Inter&#39;, sans-serif; min-width:120px; padding:8px 12px 8px 8px; background:#ffffff; border-radius:10px; box-shadow:0 5px 10px rgba(15,23,42,.1); border:1px solid #f1f5f9; position:relative;">' +
                    '<button id="' + closeBtnId + '" style="position:absolute; top:4px; right:4px; width:18px; height:18px; border:none; border-radius:50%; background:#f1f5f9; color:#0f172a; font-size:12px; line-height:18px; cursor:pointer; padding:0;">×</button>' +
                    '<div style="font-size:9px; letter-spacing:0.6px; color:#94a3b8; text-transform:uppercase;">Time</div>' +
                    '<div style="font-size:14px; font-weight:600; color:#0f172a;">' + formattedTime + '</div>' +
                    '<div style="margin-top:6px; font-size:12px; color:#64748b;">Speed</div>' +
                    '<div style="font-size:16px; font-weight:600; color:#ea580c;">' + formattedSpeed + ' km/h</div>' +
                  '</div>'
              });
              google.maps.event.addListener(infoWindow, 'domready', () => {
                const closeBtn = document.getElementById(closeBtnId);
                if (closeBtn) {
                  closeBtn.addEventListener('click', (event) => {
                    event.stopPropagation();
                    infoWindow.close();
                    if (activeInfoWindow === infoWindow) {
                      activeInfoWindow = null;
                    }
                  });
                }
              });
              marker.addListener('click', () => {
                if (activeInfoWindow) {
                  activeInfoWindow.close();
                }
                window.parent.postMessage({ type: 'markerClick', index: index }, '*');
                infoWindow.open({ anchor: marker, map });
                activeInfoWindow = infoWindow;
              });
            });
          </script>
        </body>
        </html>
      `;
    } else {
      const bounds = {
        minLat: Math.min(...historyData.map(p => parseFloat(p.latitude))) - 0.002,
        maxLat: Math.max(...historyData.map(p => parseFloat(p.latitude))) + 0.002,
        minLng: Math.min(...historyData.map(p => parseFloat(p.longitude))) - 0.002,
        maxLng: Math.max(...historyData.map(p => parseFloat(p.longitude))) + 0.002
      };

      mapHtml = `
        <!DOCTYPE html>
        <html>
        <head>
          <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
          <script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script>
          <style>
            body { margin: 0; padding: 0; }
            #map { height: 100vh; width: 100%; }
            .custom-marker {
              width: 20px;
              height: 20px;
              border-radius: 50%;
              border: 2px solid white;
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              font-size: 8px;
              font-weight: bold;
              box-shadow: 0 2px 6px rgba(0,0,0,0.3);
            }
          </style>
        </head>
        <body>
          <div id="map"></div>
          <script>
            const map = L.map('map').fitBounds([
              [${bounds.minLat}, ${bounds.minLng}],
              [${bounds.maxLat}, ${bounds.maxLng}]
            ]);
            L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
              attribution: '© OpenStreetMap contributors'
            }).addTo(map);
            const historyData = ${JSON.stringify(historyData)};
            const coordinates = historyData.map(point => [parseFloat(point.latitude), parseFloat(point.longitude)]);
            const polyline = L.polyline(coordinates, {
              color: '#0f172a',
              weight: 2.5,
              opacity: 0.9,
              dashArray: '8, 6'
            }).addTo(map);
            const toRad = (deg) => (deg * Math.PI) / 180;
            const computeBearing = (lat1, lng1, lat2, lng2) => {
              const φ1 = toRad(lat1);
              const φ2 = toRad(lat2);
              const Δλ = toRad(lng2 - lng1);
              const y = Math.sin(Δλ) * Math.cos(φ2);
              const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
              return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
            };
            const addSegmentArrows = () => {
              if (!Array.isArray(historyData) || historyData.length < 2) return;
              historyData.forEach((point, index) => {
                if (index === historyData.length - 1) return;
                const nextPoint = historyData[index + 1];
                const lat1 = parseFloat(point.latitude);
                const lng1 = parseFloat(point.longitude);
                const lat2 = parseFloat(nextPoint.latitude);
                const lng2 = parseFloat(nextPoint.longitude);
                if ([lat1, lng1, lat2, lng2].some(value => Number.isNaN(value))) return;
                const midLat = (lat1 + lat2) / 2;
                const midLng = (lng1 + lng2) / 2;
                const rotation = computeBearing(lat1, lng1, lat2, lng2);
                const arrowIcon = L.divIcon({
                  html: '<div style="width:0;height:0;border-top:6px solid transparent;border-bottom:6px solid transparent;border-left:10px solid #0f172a; transform: rotate(' + rotation + 'deg); transform-origin: 50% 50%;"></div>',
                  className: 'leaflet-direction-arrow',
                  iconSize: [20, 20],
                  iconAnchor: [10, 10]
                });
                L.marker([midLat, midLng], { icon: arrowIcon, interactive: false }).addTo(map);
              });
            };
            addSegmentArrows();
            let activeLeafletPopup = null;
            historyData.forEach((point, index) => {
              const lat = parseFloat(point.latitude);
              const lng = parseFloat(point.longitude);
              const isStart = index === 0;
              const isEnd = index === historyData.length - 1;
              const color = isStart ? '#22c55e' : isEnd ? '#ef4444' : '#215F9A';
              const label = isStart ? 'S' : isEnd ? 'E' : (index + 1);
              const closeBtnId = 'leaflet-close-' + index + '-' + Math.random().toString(36).slice(2, 7);
              const formattedTime = new Date(point.timestamp).toLocaleTimeString();
              const rawSpeed = point.speed ?? 0;
              const numericSpeed = typeof rawSpeed === 'number' ? rawSpeed : Number(rawSpeed || 0);
              const formattedSpeed = (Number.isFinite(numericSpeed) ? numericSpeed : 0).toFixed(2);
              const marker = L.marker([lat, lng], {
                icon: L.divIcon({
                  html: '<div class="custom-marker" style="background: ' + color + '">' + label + '</div>',
                  className: 'custom-div-icon',
                  iconSize: [20, 20],
                  iconAnchor: [10, 10]
                })
              }).addTo(map);
              marker.bindPopup(
                '<div style="font-family:&#39;Inter&#39;,sans-serif; min-width:120px; padding:8px 12px 8px 8px; background:#ffffff; border-radius:10px; box-shadow:0 5px 10px rgba(15,23,42,.1); border:1px solid #f1f5f9; position:relative;">' +
                  '<button id="' + closeBtnId + '" style="position:absolute; top:4px; right:4px; width:18px; height:18px; border:none; border-radius:50%; background:#f1f5f9; color:#0f172a; font-size:12px; line-height:18px; cursor:pointer; padding:0;">×</button>' +
                  '<div style="font-size:9px; letter-spacing:0.6px; color:#94a3b8; text-transform:uppercase;">Time</div>' +
                  '<div style="font-size:14px; font-weight:600; color:#0f172a;">' + formattedTime + '</div>' +
                  '<div style="margin-top:6px; font-size:12px; color:#64748b;">Speed</div>' +
                  '<div style="font-size:16px; font-weight:600; color:#ea580c;">' + formattedSpeed + ' km/h</div>' +
                '</div>'
              );
              marker.on('click', () => {
                if (activeLeafletPopup) {
                  activeLeafletPopup.remove();
                }
                activeLeafletPopup = marker.getPopup();
                marker.openPopup();
                window.parent.postMessage({type: 'markerClick', index: index}, '*');
              });
              marker.on('popupopen', () => {
                const closeBtn = document.getElementById(closeBtnId);
                if (closeBtn) {
                  closeBtn.addEventListener('click', (event) => {
                    event.stopPropagation();
                    marker.closePopup();
                    activeLeafletPopup = null;
                  });
                }
              });
            });
          </script>
        </body>
        </html>
      `;
    }

    if (mapRef.current) {
      mapRef.current.srcdoc = mapHtml;
    }

    const handleMessage = (event) => {
      if (event.data.type === 'markerClick' && onPointClick) {
        onPointClick(event.data.index);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [historyData]);

  if (!historyData || historyData.length === 0) {
    return (
      <div style={{ 
        height: '300px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#f8fafc',
        borderRadius: '8px',
        color: '#64748b'
      }}>
        No location data to display
      </div>
    );
  }

  return (
    <iframe
      ref={mapRef}
      style={{
        width: '100%',
        height: '100%',
        border: 'none',
        borderRadius: '0',
        boxShadow: 'none'
      }}
      title="Tracking History Map"
    />
  );
};

export default TrackingMap;